import type { RoofvizPacket } from "./contract.ts";
import type { GutterSpec } from "./gutters.ts";

export type RoofInsightId = "roof" | "pitch" | "junctions" | "rainwater";

export interface RoofInsight {
  id: RoofInsightId;
  tabLabel: string;
  label: string;
  value: string;
  description: string;
}

function formatMetres(value: number): string {
  return `${value.toFixed(1)} m`;
}

export function buildRoofInsights(packet: RoofvizPacket, gutter: GutterSpec): RoofInsight[] {
  const pitches = [...new Set(packet.planes.map((plane) => plane.pitchDeg))];
  const pitchValue = pitches.length === 1
    ? `${pitches[0].toFixed(1)}°`
    : `${Math.min(...pitches).toFixed(1)}–${Math.max(...pitches).toFixed(1)}°`;
  const junctionLength = packet.aggregates.ridgeM + packet.aggregates.hipM + packet.aggregates.valleyM;
  return [
    {
      id: "roof",
      tabLabel: "Roof",
      label: "Mapped roof area",
      value: `${packet.aggregates.surfaceAreaM2.toFixed(0)} m²`,
      description: `${packet.planes.length} roof planes form the digital roof shown here.`,
    },
    {
      id: "pitch",
      tabLabel: "Pitch",
      label: "Roof pitch",
      value: pitchValue,
      description: "Long-run sheets follow the fall from the high roof lines towards the gutters.",
    },
    {
      id: "junctions",
      tabLabel: "Edges",
      label: "Finished roof junctions",
      value: formatMetres(junctionLength),
      description: `${formatMetres(packet.aggregates.ridgeM)} ridge · ${formatMetres(packet.aggregates.hipM)} hips · ${formatMetres(packet.aggregates.valleyM)} valley.`,
    },
    {
      id: "rainwater",
      tabLabel: "Gutters",
      label: gutter.name,
      value: formatMetres(packet.aggregates.eaveM),
      description: "Colour-matched visual option. Final capacity, fall, outlets and downpipes are confirmed for the job.",
    },
  ];
}
